import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';

export interface MoreMenuItem {
  label: string;
  onSelect: () => void;
}

/**
 * A "More" button that folds secondary actions into a dropdown, for rows of buttons that
 * do not fit a phone's width.
 *
 * Escape is stopped here, so a page that binds Escape on the window (the recipe view goes
 * back to its list) sees it only once the menu is already closed.
 */
export function MoreMenu({ items, label = 'More' }: { items: MoreMenuItem[]; label?: string }) {
  const [open, setOpen] = useState(false);
  // The row wraps on a phone, so More can land at either edge; the menu opens toward
  // whichever side has the room.
  const [alignEnd, setAlignEnd] = useState(true);
  const menuId = useId();
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);

  // Focus moves into the menu so arrow keys and Escape reach it straight away.
  useEffect(() => {
    if (!open) return;
    menu.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus();

    function onPointerDown(event: PointerEvent) {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  function close() {
    setOpen(false);
    trigger.current?.focus();
  }

  function onMenuKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const menuItems = Array.from(
      menu.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? [],
    );
    const index = menuItems.indexOf(document.activeElement as HTMLButtonElement);
    let next: HTMLButtonElement | undefined;
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        close();
        return;
      case 'Tab':
        setOpen(false);
        return;
      case 'ArrowDown':
        next = menuItems[(index + 1) % menuItems.length];
        break;
      case 'ArrowUp':
        next = menuItems[(index - 1 + menuItems.length) % menuItems.length];
        break;
      case 'Home':
        next = menuItems[0];
        break;
      case 'End':
        next = menuItems[menuItems.length - 1];
        break;
      default:
        return;
    }
    event.preventDefault();
    next?.focus();
  }

  return (
    <div className="more-menu" ref={root}>
      <button
        type="button"
        className="btn"
        ref={trigger}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => {
          const rect = trigger.current?.getBoundingClientRect();
          if (rect != null) setAlignEnd(rect.left + rect.width / 2 > window.innerWidth / 2);
          setOpen((value) => !value);
        }}
      >
        {label}
      </button>
      {open && (
        <div
          className={alignEnd ? 'more-menu-list align-end' : 'more-menu-list'}
          id={menuId}
          role="menu"
          aria-label={label}
          ref={menu}
          onKeyDown={onMenuKeyDown}
        >
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              className="more-menu-item"
              onClick={() => {
                close();
                item.onSelect();
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
